import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Avatar, Box, Button, CircularProgress, Dialog, IconButton } from '@mui/material';
import { Close, OpenInNew } from '@mui/icons-material';
import { formatInTimeZone } from 'date-fns-tz';
import { apiService } from '../../services/apiService';
import { buildCurrentPath } from '../../utils/backNavigation';
import { isExpiredBeforeToday } from '../../utils/packExpiry';
import { SCHED, avatarTint, btnPrimarySx, btnSx, iconBtnSx } from './scheduleTheme';
import BookingDetailsSection from './BookingDetailsSection';

const IST_TIMEZONE = 'Asia/Kolkata';

interface MemberDetailDialogProps {
  /** null = closed. */
  member: { _id: string; name?: string; image?: string } | null;
  /** The booking that was clicked — shown in full, like the Booking Details page. */
  bookingId?: string;
  /** Session instructor (not carried on the booking payload). */
  instructorName?: string;
  onClose: () => void;
}

const initials = (name?: string) =>
  (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');

const fmt = (d: string | undefined, pattern: string) => (d ? formatInTimeZone(new Date(d), IST_TIMEZONE, pattern) : '');

/**
 * A member's essentials, opened from the schedule drawer without leaving the
 * calendar. Same calls as the full profile page (getUserById, pack history,
 * booking count — all studio-scoped for studio roles), with a link through
 * to that page for everything else.
 */
const MemberDetailDialog: React.FC<MemberDetailDialogProps> = ({ member, bookingId, instructorName, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<any>(null);
  const [booking, setBooking] = useState<any>(null);
  const [packs, setPacks] = useState<any[]>([]);
  const [bookingTotal, setBookingTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const memberId = member?._id;

  useEffect(() => {
    if (!memberId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUser(null);
    setBooking(null);
    setPacks([]);
    Promise.all([
      apiService.getUserById(memberId),
      apiService.getUserPackHistory(memberId, { page: 1, pageSize: 10, status: 'active' }).catch(() => null),
      apiService.getUserBookingHistory(memberId, { page: 1, pageSize: 1 }).catch(() => null),
      bookingId ? apiService.getBookingById(bookingId).catch(() => null) : Promise.resolve(null),
    ])
      .then(([u, pk, bk, bd]) => {
        if (cancelled) return;
        setUser(u?.data || null);
        setBooking(bd?.booking || null);
        setPacks((pk?.purchasedPacks || []).filter((p: any) => !isExpiredBeforeToday(p.expiryDate) && !isExpiredBeforeToday(p.packEndDate)));
        setBookingTotal(bk?.count ?? 0);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err.message || 'Failed to load member');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [memberId, bookingId]);

  const name = user?.name || member?.name || 'Member';
  const contact = [user?.phoneNumber, user?.email].filter(Boolean).join(' · ');
  const about = [user?.gender, user?.age ? `${user.age} years` : null, user?.createdAt ? `Member since ${fmt(user.createdAt, 'MMMM yyyy')}` : null]
    .filter(Boolean)
    .join(' · ');

  const sectionTitle = (label: string) => (
    <Box component="h3" sx={{ m: 0, mb: '4px', fontSize: '16px', fontWeight: 700 }}>
      {label}
    </Box>
  );
  const rowSx = { display: 'flex', alignItems: 'center', gap: '14px', minHeight: 58, borderBottom: `1px solid ${SCHED.rowLine}` };

  return (
    <Dialog
      open={Boolean(member)}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 640,
          maxWidth: 'calc(100% - 32px)',
          borderRadius: '20px',
          fontFamily: SCHED.font,
          color: SCHED.ink,
          boxShadow: '0 20px 50px rgba(40,20,26,0.18)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100% - 64px)',
        },
      }}
      slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(28,24,26,0.40)' } } }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px', p: { xs: '22px 20px 16px', sm: '26px 28px 18px' }, borderBottom: `1px solid ${SCHED.track}` }}>
        <Avatar
          src={user?.image || member?.image}
          sx={{ width: 56, height: 56, fontSize: '18px', fontWeight: 700, fontFamily: SCHED.font, backgroundColor: avatarTint(memberId || name), color: SCHED.ink }}
        >
          {initials(name)}
        </Avatar>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flexGrow: 1, minWidth: 0 }}>
          <Box component="h2" sx={{ m: 0, fontSize: '22px', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.25 }}>
            {name}
          </Box>
          {contact && <Box component="span" sx={{ fontSize: '14px', color: SCHED.inkSoft, wordBreak: 'break-word' }}>{contact}</Box>}
          {about && <Box component="span" sx={{ fontSize: '13px', color: SCHED.muted }}>{about}</Box>}
        </Box>
        <IconButton onClick={onClose} aria-label="Close" sx={{ ...iconBtnSx(40), border: 0, backgroundColor: SCHED.hover, '&:hover': { backgroundColor: SCHED.track } }}>
          <Close sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', p: { xs: '16px 20px', sm: '18px 28px' }, overflowY: 'auto', flexGrow: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} sx={{ color: SCHED.muted }} />
          </Box>
        ) : (
          <>
            {booking && <BookingDetailsSection booking={booking} instructorName={instructorName} />}

            {booking && <Box sx={{ borderTop: `1px solid ${SCHED.track}` }} />}

            <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Box component="span" sx={{ fontSize: '14px', fontWeight: 600, p: '8px 12px', borderRadius: '12px', backgroundColor: SCHED.hover }}>
                {bookingTotal} {bookingTotal === 1 ? 'booking' : 'bookings'}
              </Box>
              <Box component="span" sx={{ fontSize: '14px', fontWeight: 600, p: '8px 12px', borderRadius: '12px', backgroundColor: SCHED.successBg, color: SCHED.successText }}>
                {packs.length} active {packs.length === 1 ? 'pack' : 'packs'}
              </Box>
            </Box>

            <Box>
              {sectionTitle('Active packs')}
              {packs.length === 0 ? (
                <Box sx={{ fontSize: '14px', color: SCHED.muted, py: '10px' }}>No active packs.</Box>
              ) : (
                packs.map((p) => {
                  const ends = p.expiryDate || p.packEndDate;
                  const left = p.classesLeft != null && p.totalClasses != null ? `${p.classesLeft} of ${p.totalClasses} left` : p.classesLeft != null ? `${p.classesLeft} left` : 'Unlimited';
                  return (
                    <Box key={p._id} sx={rowSx}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flexGrow: 1, minWidth: 0 }}>
                        <Box component="span" sx={{ fontSize: '15px', fontWeight: 600 }}>{p.title || p.pack?.title || 'Pack'}</Box>
                        <Box component="span" sx={{ fontSize: '13px', color: SCHED.muted }}>
                          {left}
                          {ends ? ` · ends ${fmt(ends, 'd MMM yyyy')}` : ''}
                        </Box>
                      </Box>
                      {p.isFrozen && (
                        <Box component="span" sx={{ fontSize: '12px', fontWeight: 700, p: '3px 10px', borderRadius: '999px', backgroundColor: SCHED.waitBg, color: SCHED.waitText }}>
                          Frozen
                        </Box>
                      )}
                    </Box>
                  );
                })
              )}
            </Box>

          </>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: { xs: '14px 20px', sm: '16px 28px' }, borderTop: `1px solid ${SCHED.track}` }}>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} sx={btnSx}>
          Close
        </Button>
        <Button
          startIcon={<OpenInNew sx={{ fontSize: 18 }} />}
          disabled={!memberId}
          onClick={() => memberId && navigate(`/users/${memberId}`, { state: { from: buildCurrentPath(location.pathname, location.search) } })}
          sx={btnPrimarySx}
        >
          Full profile
        </Button>
      </Box>
    </Dialog>
  );
};

export default MemberDetailDialog;
